/**
 * One definition of what a term planner's dates mean.
 *
 * This logic was previously written out separately in ProfileScreen (twice), in
 * HomeScreen (twice) and in the backend, and every copy drifted. Each drift
 * produced a real bug: holiday mode failed with a 500 because one copy still
 * required a non-null end_date; the "planner has ended" banner nagged while a
 * live planner was running; a planner starting today read as "Starts" instead of
 * "Started"; and creating a second planner missed the live one entirely, skipping
 * the replace warning.
 *
 * Two rules everything here obeys:
 *
 *  1. Compare YYYY-MM-DD STRINGS, never Date objects. The API returns UTC
 *     midnight, so `new Date(start) > new Date()` makes a planner starting today
 *     look like it starts tomorrow west of Greenwich and yesterday east of it.
 *
 *  2. A NULL end_date means open-ended, not missing. Legacy planners carry real
 *     end dates; planners created since the open-ended change may not.
 *
 * The backend equivalent is `activePlannerForChild` in postgres-backend.js. Keep
 * the two in step: same predicate, same ordering.
 */

export interface TermPlannerLike {
  uuid?: string;
  child_uuid?: string;
  term_name?: string;
  start_date?: string | null;
  end_date?: string | null;
  superseded_by_uuid?: string | null;
}

export type PlannerStatus = 'current' | 'upcoming' | 'replaced' | 'finished';

/** Today as YYYY-MM-DD, for string comparison against API dates. */
export const todayISO = (): string => new Date().toISOString().split('T')[0];

/** The date part of an API value, whatever shape it arrives in. */
export const dayOf = (value?: string | null): string => (value || '').split('T')[0];

/**
 * Is this planner in force on `on` (default today)? It must have started, and
 * must not have been closed off — either by its own end date or by a successor.
 */
export const isPlannerLive = (planner: TermPlannerLike, on: string = todayISO()): boolean => {
  const start = dayOf(planner.start_date);
  if (!start || start > on) return false;        // not started yet
  const end = dayOf(planner.end_date);
  return !end || end >= on;                       // open-ended, or still running
};

/** Set up for a future term, not yet in force. */
export const isPlannerUpcoming = (planner: TermPlannerLike, on: string = todayISO()): boolean =>
  dayOf(planner.start_date) > on;

/**
 * What to show the parent. `replaced` is deliberately distinct from `finished`:
 * a planner taken over by a newer one is not the same as one that ran its course,
 * and same-day replacement leaves rows whose end precedes their start.
 */
export const plannerStatus = (planner: TermPlannerLike, on: string = todayISO()): PlannerStatus => {
  // Order matters. A planner that has been superseded is STILL the one in force
  // until its successor actually starts — superseding sets an end date, it does
  // not take effect immediately. Testing superseded_by_uuid first labelled the
  // running planner "Replaced" and left the list with nothing marked Current.
  if (isPlannerLive(planner, on)) return 'current';
  if (isPlannerUpcoming(planner, on)) return 'upcoming';
  if (planner.superseded_by_uuid) return 'replaced';
  return 'finished';
};

// No status LABELS are exported on purpose. Showing the parent a four-word
// vocabulary — Current / Upcoming / Replaced / Finished — was more taxonomy than
// they asked for, and the planner's name already carries its dates. The UI marks
// only the planner in force, via isPlannerLive. These states remain for internal
// decisions such as finding a lapsed planner to warn about.

/**
 * THE planner in force for a child. Mirrors the backend ordering: an open-ended
 * planner wins, then the latest-ending, then the most recently started.
 *
 * Pass every list you have — they are merged, filtered to `childUuid` and deduped,
 * because the caller cannot know which store loaded first and one of them may hold
 * a different child's planners entirely.
 */
export const findLivePlanner = <T extends TermPlannerLike>(
  childUuid: string | undefined,
  ...sources: (T[] | undefined)[]
): T | null => {
  if (!childUuid) return null;
  const on = todayISO();
  const seen = new Set<string>();
  const candidates: T[] = [];

  for (const list of sources) {
    for (const planner of list || []) {
      if (planner.child_uuid && planner.child_uuid !== childUuid) continue;
      const key = planner.uuid || `${planner.start_date}|${planner.end_date}`;
      if (seen.has(key)) continue;
      seen.add(key);
      candidates.push(planner);
    }
  }

  const live = candidates.filter((p) => isPlannerLive(p, on));
  if (live.length === 0) return null;

  // Open-ended first, then latest end, then latest start — same as the backend.
  live.sort((a, b) => {
    const aEnd = dayOf(a.end_date);
    const bEnd = dayOf(b.end_date);
    if (!aEnd && bEnd) return -1;
    if (aEnd && !bEnd) return 1;
    if (aEnd !== bEnd) return aEnd < bEnd ? 1 : -1;
    return dayOf(a.start_date) < dayOf(b.start_date) ? 1 : -1;
  });
  return live[0];
};

/**
 * How far ahead a queued planner is worth putting on the home screen. Set a term
 * up in January for September and an un-capped banner would sit there for eight
 * months, which trains the parent to ignore it — and it is the fortnight before a
 * change that actually matters.
 */
export const UPCOMING_BANNER_DAYS = 14;

/**
 * The soonest planner queued to take over, if any.
 *
 * `withinDays` limits how far ahead to look. Omit it where distance is irrelevant
 * — the planner setup screen names a queued planner however far off it is, since
 * the parent is about to butt a new planner up against it.
 */
export const findUpcomingPlanner = <T extends TermPlannerLike>(
  planners: T[] | undefined,
  on: string = todayISO(),
  withinDays?: number
): T | null => {
  let horizon: string | null = null;
  if (typeof withinDays === 'number') {
    const [y, m, d] = on.split('-').map(Number);
    const limit = new Date(Date.UTC(y, m - 1, d + withinDays));
    horizon = limit.toISOString().split('T')[0];
  }

  const queued = (planners || [])
    .filter((p) => isPlannerUpcoming(p, on))
    .filter((p) => !horizon || dayOf(p.start_date) <= horizon)
    .sort((a, b) => (dayOf(a.start_date) < dayOf(b.start_date) ? -1 : 1));
  return queued[0] || null;
};

/**
 * A planner is named after its date range — "18 Aug - 25 Sep 2026", or with both
 * years when it spans one. The backend derives the same string and is
 * authoritative, since it can clamp the end date after the client has guessed.
 */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const plannerNameFromDates = (startValue?: string | null, endValue?: string | null): string => {
  const start = dayOf(startValue);
  if (!start) return '';
  const [sy, sm, sd] = start.split('-').map(Number);
  const end = dayOf(endValue);
  if (!end) return `From ${sd} ${MONTHS[sm - 1]} ${sy}`;
  const [ey, em, ed] = end.split('-').map(Number);
  const left = `${sd} ${MONTHS[sm - 1]}${sy === ey ? '' : ' ' + sy}`;
  return `${left} - ${ed} ${MONTHS[em - 1]} ${ey}`;
};
